import i18n, { currentLocale } from '../../../i18n';
import { dateLocale, NUMBER_LOCALE } from '../../../lib/localePrefs';
import { SOURCE_ORDER, sourceMeta } from '../data/reservationSources';
import type { ReservationSourceId } from '../types';

/**
 * Period-KPI filters for Custom groups ("reservations > 100 in the last 14
 * days"). The server (`opsCommand/getScopePeriodMetrics`) returns raw additive
 * columns per restaurant; every derived metric is computed here so the wire
 * contract stays stable when metrics are added.
 */

export type ScopeKpiFamily = 'reservations' | 'occupancy' | 'revenue';

export type ScopeKpiGroup = 'volume' | 'quality' | 'channel' | 'occupancy' | 'revenue';

export type ScopeKpiMetric =
  | 'reservations'
  | 'guests'
  | 'avgPartySize'
  | 'openDays'
  | 'noShowRate'
  | 'cancelRate'
  | 'rejected'
  | 'walkInShare'
  | 'onlineShare'
  | 'sourceShare'
  | 'occupancy'
  | 'revenue';

export type ScopeKpiAggregation = 'total' | 'perOpenDay';

export type ScopeKpiOperator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'between' | 'noData';

export type ScopePeriodPreset =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last14'
  | 'last28'
  | 'lastMonth'
  | 'last3Months'
  | 'custom';

export type ScopePeriod =
  | { preset: Exclude<ScopePeriodPreset, 'custom'> }
  | { preset: 'custom'; start: string; end: string };

/** Inclusive YYYY-MM-DD range. */
export type ResolvedPeriod = { start: string; end: string };

export type ScopeKpiRule = {
  id: string;
  type: 'kpi';
  metric: ScopeKpiMetric;
  aggregation: ScopeKpiAggregation;
  /** sourceShare only. */
  source?: ReservationSourceId;
  /** revenue only — no FX conversion, so a rule targets one currency. */
  currency?: string;
  period: ScopePeriod;
  operator: ScopeKpiOperator;
  value: string;
  /** Upper bound for `between` (inclusive). */
  value2?: string;
};

export type ScopeKpiUnit = 'count' | 'pct' | 'ratio' | 'currency' | 'days';

export type ScopeKpiMetricDef = {
  id: ScopeKpiMetric;
  group: ScopeKpiGroup;
  family: ScopeKpiFamily;
  unit: ScopeKpiUnit;
  aggregations: ScopeKpiAggregation[];
  /** Decimals per aggregation; values are compared after rounding to what's shown. */
  decimals: Partial<Record<ScopeKpiAggregation, number>>;
};

const TOTAL_ONLY: ScopeKpiAggregation[] = ['total'];
const TOTAL_OR_DAILY: ScopeKpiAggregation[] = ['total', 'perOpenDay'];

export const SCOPE_KPI_METRICS: ScopeKpiMetricDef[] = [
  { id: 'reservations', group: 'volume', family: 'reservations', unit: 'count', aggregations: TOTAL_OR_DAILY, decimals: { total: 0, perOpenDay: 1 } },
  { id: 'guests', group: 'volume', family: 'reservations', unit: 'count', aggregations: TOTAL_OR_DAILY, decimals: { total: 0, perOpenDay: 1 } },
  { id: 'avgPartySize', group: 'volume', family: 'reservations', unit: 'ratio', aggregations: TOTAL_ONLY, decimals: { total: 1 } },
  { id: 'openDays', group: 'volume', family: 'reservations', unit: 'days', aggregations: TOTAL_ONLY, decimals: { total: 0 } },
  { id: 'noShowRate', group: 'quality', family: 'reservations', unit: 'pct', aggregations: TOTAL_ONLY, decimals: { total: 1 } },
  { id: 'cancelRate', group: 'quality', family: 'reservations', unit: 'pct', aggregations: TOTAL_ONLY, decimals: { total: 1 } },
  { id: 'rejected', group: 'quality', family: 'reservations', unit: 'count', aggregations: TOTAL_OR_DAILY, decimals: { total: 0, perOpenDay: 1 } },
  { id: 'walkInShare', group: 'channel', family: 'reservations', unit: 'pct', aggregations: TOTAL_ONLY, decimals: { total: 1 } },
  { id: 'onlineShare', group: 'channel', family: 'reservations', unit: 'pct', aggregations: TOTAL_ONLY, decimals: { total: 1 } },
  { id: 'sourceShare', group: 'channel', family: 'reservations', unit: 'pct', aggregations: TOTAL_ONLY, decimals: { total: 1 } },
  // Matches Performance's occupancy_pct (js_round to an integer).
  { id: 'occupancy', group: 'occupancy', family: 'occupancy', unit: 'pct', aggregations: TOTAL_ONLY, decimals: { total: 0 } },
  { id: 'revenue', group: 'revenue', family: 'revenue', unit: 'currency', aggregations: TOTAL_OR_DAILY, decimals: { total: 0, perOpenDay: 0 } },
];

export const SCOPE_KPI_GROUP_ORDER: ScopeKpiGroup[] = [
  'volume',
  'quality',
  'channel',
  'occupancy',
  'revenue',
];

export const SCOPE_PERIOD_PRESETS: ScopePeriodPreset[] = [
  'today',
  'yesterday',
  'last7',
  'last14',
  'last28',
  'lastMonth',
  'last3Months',
  'custom',
];

export const SCOPE_KPI_OPERATORS: ScopeKpiOperator[] = [
  'gt',
  'gte',
  'lt',
  'lte',
  'eq',
  'between',
  'noData',
];

/** Server cap on a single evaluation window (inclusive days). */
export const MAX_SCOPE_PERIOD_DAYS = 366;

/** Online = guest-facing booking channels; Assistent AI stays its own source. */
const ONLINE_SOURCES: ReservationSourceId[] = ['widget', 'reserveWithGoogle'];

export const SCOPE_SHARE_SOURCES: ReservationSourceId[] = SOURCE_ORDER;

export function kpiMetricDef(metric: ScopeKpiMetric): ScopeKpiMetricDef {
  return SCOPE_KPI_METRICS.find(m => m.id === metric) ?? SCOPE_KPI_METRICS[0];
}

export function isScopeKpiRule(rule: { type?: string }): rule is ScopeKpiRule {
  return rule.type === 'kpi';
}

// ---------------------------------------------------------------------------
// Periods

/** The reference calendar for presets — the fleet runs on Central European time. */
export const SCOPE_PERIOD_TIMEZONE = 'Europe/Berlin';

/** Today's date (YYYY-MM-DD) in the reference timezone. */
export function scopeToday(now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: SCOPE_PERIOD_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function isoToUtc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function utcToIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDaysIso(iso: string, days: number): string {
  const d = isoToUtc(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return utcToIso(d);
}

/** Inclusive day count of a resolved period. */
export function periodDayCount(period: ResolvedPeriod): number {
  return (
    Math.round(
      (isoToUtc(period.end).getTime() - isoToUtc(period.start).getTime()) / 86_400_000,
    ) + 1
  );
}

function monthStartIso(iso: string, monthDelta: number): string {
  const d = isoToUtc(iso);
  return utcToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + monthDelta, 1)));
}

/**
 * Relative presets resolve against "now" so a re-run rolls forward; rolling
 * windows end yesterday (completed days, like Performance's presets).
 */
export function resolveScopePeriod(period: ScopePeriod, now: Date = new Date()): ResolvedPeriod {
  const today = scopeToday(now);
  const yesterday = addDaysIso(today, -1);
  switch (period.preset) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday':
      return { start: yesterday, end: yesterday };
    case 'last7':
      return { start: addDaysIso(yesterday, -6), end: yesterday };
    case 'last14':
      return { start: addDaysIso(yesterday, -13), end: yesterday };
    case 'last28':
      return { start: addDaysIso(yesterday, -27), end: yesterday };
    case 'lastMonth': {
      const thisMonth = monthStartIso(today, 0);
      return { start: monthStartIso(today, -1), end: addDaysIso(thisMonth, -1) };
    }
    case 'last3Months': {
      const thisMonth = monthStartIso(today, 0);
      return { start: monthStartIso(today, -3), end: addDaysIso(thisMonth, -1) };
    }
    case 'custom':
      return { start: period.start, end: period.end };
  }
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export type ScopePeriodError = 'invalid' | 'future' | 'tooLong';

export function validateResolvedPeriod(
  period: ResolvedPeriod,
  now: Date = new Date(),
): ScopePeriodError | null {
  if (!ISO_RE.test(period.start) || !ISO_RE.test(period.end) || period.start > period.end) {
    return 'invalid';
  }
  if (period.end > scopeToday(now)) {
    return 'future';
  }
  if (periodDayCount(period) > MAX_SCOPE_PERIOD_DAYS) {
    return 'tooLong';
  }
  return null;
}

export function periodKey(period: ResolvedPeriod): string {
  return `${period.start}_${period.end}`;
}

function fmtDay(iso: string, withYear: boolean): string {
  return isoToUtc(iso).toLocaleDateString(dateLocale(currentLocale()), {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

/** "29 Sep – 12 Oct" (year only when the range crosses years). */
export function formatResolvedPeriod(period: ResolvedPeriod): string {
  if (period.start === period.end) {
    return fmtDay(period.start, false);
  }
  const crossYear = period.start.slice(0, 4) !== period.end.slice(0, 4);
  return `${fmtDay(period.start, crossYear)} – ${fmtDay(period.end, crossYear)}`;
}

export function periodPresetLabel(preset: ScopePeriodPreset): string {
  return i18n.t(`chrome:scopeFilters.kpi.periods.${preset}`);
}

// ---------------------------------------------------------------------------
// Server payload + derivation

export type ScopePeriodMetricsColumn =
  | 'booked'
  | 'covers'
  | 'served'
  | 'noshow'
  | 'cancel'
  | 'rejected'
  | 'noshowBase'
  | 'cancelBase'
  | 'openDays'
  | 'covWidget'
  | 'covReserveWithGoogle'
  | 'covAssistentAi'
  | 'covStaff'
  | 'covWalkIn'
  | 'covIntegration'
  | 'covOther'
  | 'coverCapacity'
  | 'revenue'
  | 'currency';

export type ScopePeriodMetricsPayload = {
  start: string;
  end: string;
  families: ScopeKpiFamily[];
  evaluatedAt: string;
  /** Only restaurants with rows in the period; absent = no activity. */
  ids: string[];
  cols: Partial<Record<ScopePeriodMetricsColumn, (number | string | null)[]>>;
  currencies: string[];
};

export type ScopePeriodMetricsRequest = {
  start: string;
  end: string;
  families: ScopeKpiFamily[];
};

export type ScopeKpiIndex = {
  payload: ScopePeriodMetricsPayload;
  position: Map<string, number>;
};

export function indexScopePeriodMetrics(payload: ScopePeriodMetricsPayload): ScopeKpiIndex {
  return {
    payload,
    position: new Map(payload.ids.map((id, i) => [id, i])),
  };
}

const SOURCE_COLUMN: Record<ReservationSourceId, ScopePeriodMetricsColumn> = {
  widget: 'covWidget',
  reserveWithGoogle: 'covReserveWithGoogle',
  assistentAi: 'covAssistentAi',
  staff: 'covStaff',
  walkIn: 'covWalkIn',
  integration: 'covIntegration',
  other: 'covOther',
  unattributed: 'covOther',
};

function num(index: ScopeKpiIndex, col: ScopePeriodMetricsColumn, at: number | undefined): number {
  if (at === undefined) return 0;
  const v = index.payload.cols[col]?.[at];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function nullableNum(
  index: ScopeKpiIndex,
  col: ScopePeriodMetricsColumn,
  at: number | undefined,
): number | null {
  if (at === undefined) return null;
  const v = index.payload.cols[col]?.[at];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function ratio(numerator: number, denominator: number, scale = 1): number | null {
  return denominator > 0 ? (numerator / denominator) * scale : null;
}

function roundTo(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

export type ScopeKpiValue = {
  /** Rounded to the displayed precision; null = no data (zero denominator, no POS…). */
  value: number | null;
  /** Reservations behind a rate, shown next to it so small samples are visible. */
  base?: number;
};

/**
 * Derive a rule's metric for one restaurant. Counts with no activity are a
 * real 0; ratios with a zero denominator are null and only match "has no data".
 */
export function scopeKpiValue(
  index: ScopeKpiIndex,
  restaurantId: string,
  rule: Pick<ScopeKpiRule, 'metric' | 'aggregation' | 'source' | 'currency'>,
): ScopeKpiValue {
  const def = kpiMetricDef(rule.metric);
  const aggregation = def.aggregations.includes(rule.aggregation)
    ? rule.aggregation
    : def.aggregations[0];
  const at = index.position.get(restaurantId);
  const covers = num(index, 'covers', at);
  const openDays = num(index, 'openDays', at);

  const perDay = (total: number | null): number | null =>
    total == null ? null : aggregation === 'perOpenDay' ? ratio(total, openDays) : total;

  let raw: number | null;
  let base: number | undefined;
  switch (rule.metric) {
    case 'reservations':
      // Confirmed reservations, as Performance's Reservations tile counts
      // them (backend semantics.CONFIRMED_STATES). The scope column `served`
      // holds exactly those states; `booked` is every request.
      raw = perDay(num(index, 'served', at));
      break;
    case 'guests':
      raw = perDay(covers);
      break;
    case 'rejected':
      raw = perDay(num(index, 'rejected', at));
      break;
    case 'openDays':
      raw = openDays;
      break;
    case 'avgPartySize':
      raw = ratio(covers, num(index, 'served', at));
      break;
    // Shared rate bases (backend semantics.py): no-shows over served +
    // no-shows, guest cancellations over served + no-shows + cancellations.
    case 'noShowRate': {
      const noshowBase = num(index, 'noshowBase', at);
      raw = ratio(num(index, 'noshow', at), noshowBase, 100);
      base = noshowBase;
      break;
    }
    case 'cancelRate': {
      const cancelBase = num(index, 'cancelBase', at);
      raw = ratio(num(index, 'cancel', at), cancelBase, 100);
      base = cancelBase;
      break;
    }
    case 'walkInShare':
      raw = ratio(num(index, 'covWalkIn', at), covers, 100);
      break;
    case 'onlineShare':
      raw = ratio(
        ONLINE_SOURCES.reduce((sum, s) => sum + num(index, SOURCE_COLUMN[s], at), 0),
        covers,
        100,
      );
      break;
    case 'sourceShare':
      raw = ratio(num(index, SOURCE_COLUMN[rule.source ?? 'widget'], at), covers, 100);
      break;
    case 'occupancy': {
      const capacity = nullableNum(index, 'coverCapacity', at);
      raw = capacity != null ? ratio(covers, capacity, 100) : null;
      break;
    }
    case 'revenue': {
      const currency = at === undefined ? null : index.payload.cols.currency?.[at];
      const revenue = nullableNum(index, 'revenue', at);
      raw = revenue == null || !currency || currency !== rule.currency ? null : perDay(revenue);
      break;
    }
  }
  const decimals = def.decimals[aggregation] ?? 0;
  return { value: raw == null ? null : roundTo(raw, decimals), base };
}

export function matchKpiValue(value: number | null, rule: ScopeKpiRule): boolean {
  if (rule.operator === 'noData') {
    return value == null;
  }
  if (value == null) {
    return false;
  }
  const target = Number(rule.value);
  if (rule.value.trim() === '' || !Number.isFinite(target)) {
    return false;
  }
  switch (rule.operator) {
    case 'gt':
      return value > target;
    case 'gte':
      return value >= target;
    case 'lt':
      return value < target;
    case 'lte':
      return value <= target;
    case 'eq':
      return value === target;
    case 'between': {
      const upper = Number(rule.value2);
      if (!rule.value2?.trim() || !Number.isFinite(upper)) return false;
      const lo = Math.min(target, upper);
      const hi = Math.max(target, upper);
      return value >= lo && value <= hi;
    }
  }
}

// ---------------------------------------------------------------------------
// Rules

export function createKpiRule(metric: ScopeKpiMetric = 'reservations'): ScopeKpiRule {
  const def = kpiMetricDef(metric);
  return {
    id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type: 'kpi',
    metric: def.id,
    aggregation: def.aggregations[0],
    ...(def.id === 'sourceShare' ? { source: 'walkIn' as const } : {}),
    ...(def.family === 'revenue' ? { currency: 'EUR' } : {}),
    period: { preset: 'last14' },
    operator: 'gt',
    value: defaultKpiThreshold(def.id),
  };
}

function defaultKpiThreshold(metric: ScopeKpiMetric): string {
  switch (metric) {
    case 'reservations':
      return '100';
    case 'guests':
      return '200';
    case 'avgPartySize':
      return '2';
    case 'openDays':
      return '7';
    case 'noShowRate':
    case 'cancelRate':
      return '5';
    case 'rejected':
      return '0';
    case 'walkInShare':
    case 'onlineShare':
    case 'sourceShare':
      return '20';
    case 'occupancy':
      return '50';
    case 'revenue':
      return '10000';
  }
}

/** Keep a rule internally consistent after its metric changes. */
export function retargetKpiRule(rule: ScopeKpiRule, metric: ScopeKpiMetric): ScopeKpiRule {
  const def = kpiMetricDef(metric);
  return {
    ...rule,
    metric,
    aggregation: def.aggregations.includes(rule.aggregation) ? rule.aggregation : def.aggregations[0],
    source: def.id === 'sourceShare' ? (rule.source ?? 'walkIn') : undefined,
    currency: def.family === 'revenue' ? (rule.currency ?? 'EUR') : undefined,
    value: defaultKpiThreshold(metric),
    value2: undefined,
  };
}

export function kpiMetricLabel(metric: ScopeKpiMetric): string {
  return i18n.t(`chrome:scopeFilters.kpi.metrics.${metric}`);
}

export function kpiGroupLabel(group: ScopeKpiGroup): string {
  return i18n.t(`chrome:scopeFilters.kpi.groups.${group}`);
}

export function kpiAggregationLabel(aggregation: ScopeKpiAggregation): string {
  return i18n.t(`chrome:scopeFilters.kpi.aggregations.${aggregation}`);
}

export function kpiOperatorLabel(op: ScopeKpiOperator): string {
  return i18n.t(`chrome:scopeFilters.ops.${op}`);
}

/** Short metric name incl. source / aggregation qualifiers, e.g. "Share: Walk-in". */
export function kpiRuleMetricName(rule: Pick<ScopeKpiRule, 'metric' | 'aggregation' | 'source'>): string {
  const def = kpiMetricDef(rule.metric);
  let name = kpiMetricLabel(rule.metric);
  if (rule.metric === 'sourceShare' && rule.source) {
    name = `${name}: ${sourceMeta(rule.source).label}`;
  }
  if (def.aggregations.length > 1 && rule.aggregation === 'perOpenDay') {
    name = `${name} ${kpiAggregationLabel('perOpenDay')}`;
  }
  return name;
}

export function kpiUnitSuffix(rule: Pick<ScopeKpiRule, 'metric' | 'currency'>): string {
  const def = kpiMetricDef(rule.metric);
  if (def.unit === 'pct') return '%';
  if (def.unit === 'currency') return rule.currency ?? '';
  return '';
}

export function formatKpiValue(
  value: number | null,
  rule: Pick<ScopeKpiRule, 'metric' | 'aggregation' | 'currency'>,
): string {
  if (value == null) {
    return i18n.t('chrome:scopeFilters.kpi.noData');
  }
  const def = kpiMetricDef(rule.metric);
  const decimals = def.decimals[rule.aggregation] ?? def.decimals.total ?? 0;
  const formatted = value.toLocaleString(NUMBER_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  const suffix = kpiUnitSuffix(rule);
  if (!suffix) return formatted;
  return def.unit === 'pct' ? `${formatted}%` : `${formatted} ${suffix}`;
}

function periodSummary(period: ScopePeriod, resolved?: ResolvedPeriod): string {
  if (period.preset === 'custom') {
    return formatResolvedPeriod(resolved ?? { start: period.start, end: period.end });
  }
  const label = periodPresetLabel(period.preset);
  return resolved ? `${label} (${formatResolvedPeriod(resolved)})` : label;
}

/** "Reservations > 100 · Last 14 days (29 Sep – 12 Oct)" — collapsed list and recipe banner. */
export function summarizeKpiRule(rule: ScopeKpiRule, resolved?: ResolvedPeriod): string {
  const name = kpiRuleMetricName(rule);
  const op = kpiOperatorLabel(rule.operator);
  const suffix = kpiUnitSuffix(rule);
  const withUnit = (v: string | undefined) =>
    suffix === '%' ? `${v ?? ''}%` : suffix ? `${v ?? ''} ${suffix}` : (v ?? '');
  let condition: string;
  if (rule.operator === 'noData') {
    condition = `${name} ${op}`;
  } else if (rule.operator === 'between') {
    condition = `${name} ${op} ${withUnit(rule.value)} – ${withUnit(rule.value2)}`;
  } else {
    condition = `${name} ${op} ${withUnit(rule.value)}`;
  }
  return `${condition} · ${periodSummary(rule.period, resolved)}`;
}

/** Families the server must compute for a set of rules sharing one period. */
export function familiesForRules(rules: ScopeKpiRule[]): ScopeKpiFamily[] {
  const set = new Set<ScopeKpiFamily>(['reservations']);
  for (const r of rules) set.add(kpiMetricDef(r.metric).family);
  return (['reservations', 'occupancy', 'revenue'] as const).filter(f => set.has(f));
}

/** How many distinct periods one filter set may evaluate at once. */
export const MAX_SCOPE_KPI_PERIODS = 3;

export type ScopeKpiRuleError = ScopePeriodError | 'tooManyPeriods';

export type ScopeKpiPlan = {
  /** One request per distinct resolved period (≤ MAX_SCOPE_KPI_PERIODS). */
  requests: ScopePeriodMetricsRequest[];
  /** rule id → resolved period, for every KPI rule. */
  resolvedByRule: Map<string, ResolvedPeriod>;
  /** rule id → index into `requests`, for rules that can be evaluated. */
  requestByRule: Map<string, number>;
  /** rule id → validation problem (invalid period, too many periods). */
  errors: Map<string, ScopeKpiRuleError>;
};

export function planScopeKpiRequests(rules: ScopeKpiRule[], now: Date = new Date()): ScopeKpiPlan {
  const resolvedByRule = new Map<string, ResolvedPeriod>();
  const requestByRule = new Map<string, number>();
  const errors = new Map<string, ScopeKpiRuleError>();
  const slots: { key: string; period: ResolvedPeriod; rules: ScopeKpiRule[] }[] = [];
  for (const rule of rules) {
    const resolved = resolveScopePeriod(rule.period, now);
    resolvedByRule.set(rule.id, resolved);
    const problem = validateResolvedPeriod(resolved, now);
    if (problem) {
      errors.set(rule.id, problem);
      continue;
    }
    const key = periodKey(resolved);
    let slotIndex = slots.findIndex(s => s.key === key);
    if (slotIndex < 0) {
      if (slots.length >= MAX_SCOPE_KPI_PERIODS) {
        errors.set(rule.id, 'tooManyPeriods');
        continue;
      }
      slots.push({ key, period: resolved, rules: [] });
      slotIndex = slots.length - 1;
    }
    slots[slotIndex].rules.push(rule);
    requestByRule.set(rule.id, slotIndex);
  }
  const requests = slots.map(({ period, rules: periodRules }) => ({
    start: period.start,
    end: period.end,
    families: familiesForRules(periodRules),
  }));
  return { requests, resolvedByRule, requestByRule, errors };
}
